import { Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { isAzureBlobUrl } from '@/hooks/useBlobAccessUrl';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

import type {
	ChatAction,
	RedditPostPreview,
} from '../../../../../bridge/types/messages.ts';

import { ChatActions } from './ChatActions.tsx';
import { MessageRow } from './MessageRow.tsx';
import { getStandaloneAnchor, isRedditPostUrl } from './RedditEmbed.helpers.ts';
import { RedditEmbed } from './RedditEmbed.tsx';

const strongStyle = style({ fontWeight: 'extra-bold' });
const emStyle = style({ fontStyle: 'italic' });
const inlineCodeStyle = style({
	fontFamily: 'code',
	fontSize: 'body-sm',
	backgroundColor: 'gray-100',
	paddingX: 4,
	paddingY: 2,
	borderRadius: 'default',
});
const preStyle = style({
	fontFamily: 'code',
	fontSize: 'body-sm',
	backgroundColor: 'gray-100',
	padding: 12,
	borderRadius: 'default',
	marginY: 8,
	overflow: 'auto',
});
const h1Style = style({
	fontSize: 'heading-lg',
	fontWeight: 'extra-bold',
	marginTop: 32,
	marginBottom: 12,
});
const h2Style = style({
	fontSize: 'heading',
	fontWeight: 'extra-bold',
	marginTop: 28,
	marginBottom: 12,
});
const h3Style = style({
	fontSize: 'heading-sm',
	fontWeight: 'bold',
	marginTop: 24,
	marginBottom: 8,
});
const h4Style = style({
	fontSize: 'body-lg',
	fontWeight: 'bold',
	marginTop: 20,
	marginBottom: 8,
});
const linkStyle = style({
	color: 'accent-900',
	textDecoration: 'underline',
});
const tableStyle = style({
	width: 'full',
	borderCollapse: 'separate',
	borderSpacing: 0,
	borderWidth: 1,
	borderStyle: 'solid',
	borderColor: 'gray-200',
	borderRadius: 'default',
	overflow: 'hidden',
	marginY: 16,
	fontSize: 'body-sm',
});
const thStyle = style({
	textAlign: 'start',
	fontWeight: 'bold',
	fontSize: 'body-sm',
	textTransform: 'uppercase',
	letterSpacing: 'wide',
	paddingX: 12,
	paddingY: 8,
	borderBottomWidth: 1,
	borderBottomStyle: 'solid',
	borderColor: 'gray-300',
	backgroundColor: 'gray-100',
});
const tdStyle = style({
	paddingX: 12,
	paddingY: 8,
	borderBottomWidth: 1,
	borderBottomStyle: 'solid',
	borderColor: 'gray-200',
	verticalAlign: 'top',
});

// A markdown link and a stored permalink can differ by one trailing slash, and
// they still name the same post.
const sameRedditPost = (a: string, b: string): boolean =>
	a.replace(/\/+$/, '') === b.replace(/\/+$/, '');

export const AgentMessage = ({
	message,
	actions,
	showActions,
	isStreaming,
	tightMargins = false,
	redditPreview,
}: {
	message: string;
	actions?: ChatAction[];
	showActions: boolean;
	isStreaming: boolean;
	// Drop the leading/trailing block margins so messages sit tighter. Part of the
	// denser chat layout the strategy dock introduces, not the default spacing.
	tightMargins?: boolean;
	// The post this message was written about, stored with the message. The card
	// uses it instead of asking Reddit, which answers our servers with a 403.
	redditPreview?: RedditPostPreview;
}) => {
	const bridgeApi = useBridgeApi();
	const handleAction = (action: ChatAction) => {
		trackEvent('Agent Action Clicked', { action_label: action.label });
		bridgeApi.sendAction({
			type: 'do_add_user_message_and_force_response',
			content: action.message,
		});
	};

	return (
		<MessageRow data-testid="agent-message">
			<Text
				styles={style({
					font: 'body',
					color: 'body',
					fontSize: 'body',
					// This span is the flex item of MessageRow. A flex item defaults to
					// min-width: auto, which resolves to its widest child — so a wide code
					// block, table, or long unbreakable token stretches the span past the
					// column and the whole thread scrolls sideways. minWidth: 0 lets it
					// shrink to the column; maxWidth caps it there, so prose wraps and the
					// wide child scrolls inside its own box instead.
					maxWidth: 'full',
					minWidth: 0,
				})}
			>
				<div
					className={cn(
						tightMargins && '[&>:first-child]:mt-0 [&>:last-child]:mb-0',
					)}
				>
					<Markdown
						remarkPlugins={[remarkGfm]}
						allowedElements={[
							'p',
							'br',
							'strong',
							'em',
							'del',
							'a',
							'code',
							'pre',
							'ul',
							'ol',
							'li',
							'h1',
							'h2',
							'h3',
							'h4',
							'h5',
							'h6',
							'blockquote',
							'hr',
							'table',
							'thead',
							'tbody',
							'tr',
							'th',
							'td',
							'img',
						]}
						unwrapDisallowed={true}
						components={{
							p: ({ children }) => {
								if (!isStreaming) {
									const anchor = getStandaloneAnchor(children);
									if (anchor && isRedditPostUrl(anchor.href)) {
										const stored =
											redditPreview &&
											sameRedditPost(anchor.href, redditPreview.url)
												? redditPreview
												: undefined;
										return (
											<RedditEmbed
												url={anchor.href}
												fallbackText={anchor.text}
												storedPost={stored}
											/>
										);
									}
								}
								return <p className="my-4">{children}</p>;
							},
							strong: ({ children }) => (
								<strong className={strongStyle}>{children}</strong>
							),
							em: ({ children }) => <em className={emStyle}>{children}</em>,
							del: ({ children }) => <del>{children}</del>,
							a: ({ children, href }) => (
								<a
									href={href}
									target="_blank"
									rel="noreferrer"
									className={`${linkStyle} [overflow-wrap:anywhere]`}
								>
									{children}
								</a>
							),
							code: ({ children, className }) => {
								const isBlock = className?.startsWith('language-');
								if (isBlock) {
									return <code>{children}</code>;
								}
								return <code className={inlineCodeStyle}>{children}</code>;
							},
							img: ({ src, alt }) => {
								if (!src || !isAzureBlobUrl(src)) return null;
								return (
									<BlobImage
										src={src}
										alt={alt ?? ''}
										loading="lazy"
										className="my-4 max-w-full rounded-lg"
									/>
								);
							},
							pre: ({ children }) => <pre className={preStyle}>{children}</pre>,
							ul: ({ children }) => (
								<ul
									className="my-4 list-disc space-y-1.5 pl-6 [&_ol]:mt-1.5
										[&_ul]:mt-1.5"
								>
									{children}
								</ul>
							),
							ol: ({ children }) => (
								<ol
									className="my-4 list-decimal space-y-1.5 pl-6 [&_ol]:mt-1.5
										[&_ul]:mt-1.5"
								>
									{children}
								</ol>
							),
							li: ({ children }) => (
								<li className="leading-relaxed">{children}</li>
							),
							h1: ({ children }) => <h1 className={h1Style}>{children}</h1>,
							h2: ({ children }) => <h2 className={h2Style}>{children}</h2>,
							h3: ({ children }) => <h3 className={h3Style}>{children}</h3>,
							h4: ({ children }) => <h4 className={h4Style}>{children}</h4>,
							h5: ({ children }) => <h5 className={h4Style}>{children}</h5>,
							h6: ({ children }) => <h6 className={h4Style}>{children}</h6>,
							blockquote: ({ children }) => (
								<blockquote
									className="border-beacon-stroke bg-beacon-ivory/60 my-4
										rounded-r-sm border-l-[3px] px-4 py-2 text-gray-700 italic"
								>
									{children}
								</blockquote>
							),
							hr: () => (
								<hr className="border-beacon-stroke my-6 border-0 border-t" />
							),
							table: ({ children }) => (
								<table className={tableStyle}>{children}</table>
							),
							thead: ({ children }) => <thead>{children}</thead>,
							tbody: ({ children }) => (
								<tbody className="[&>tr:last-child>td]:border-b-0">
									{children}
								</tbody>
							),
							tr: ({ children }) => (
								<tr className="even:bg-beacon-ivory/40">{children}</tr>
							),
							th: ({ children }) => <th className={thStyle}>{children}</th>,
							td: ({ children }) => <td className={tdStyle}>{children}</td>,
						}}
					>
						{message}
					</Markdown>
				</div>
			</Text>
			{showActions && actions && actions.length > 0 && (
				<ChatActions actions={actions} onAction={handleAction} />
			)}
		</MessageRow>
	);
};
